import { ChangeEvent, FormEvent, Fragment, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import {
  createPetPlanImport,
  listPetPlanCodeAssignments,
  savePetPlanCodeAssignments,
} from '../services/petPlanImportsService';
import { isCompetenceConsolidated } from '../services/expensesConsolidationService';
import { parsePetPlanFile } from '../services/petPlanParser';
import type { PetPlanAuditItem, PetPlanRecord } from '../services/petPlanTypes';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { PetPawIcon } from '@/components/icons/PetPawIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { normalizeString } from '@/utils/stringUtils';

type CollaboratorReference = {
  id: string;
  name: string;
  cpf?: string | null;
  normalizedName: string;
  emails: string[];
  normalizedEmails: string[];
  isActive: boolean;
};

type PetPlanMatchStatus = 'auto_code' | 'auto_name' | 'manual' | 'not_found';

type PetPlanMatch = {
  record: PetPlanRecord;
  collaboratorId: string | null;
  status: PetPlanMatchStatus;
};

const normalizeCodeKey = (code: string): string => {
  if (code == null) return '';
  const raw = code.toString().trim();
  if (!raw) return '';
  if (raw.includes('@')) {
    return raw.toLowerCase();
  }
  return raw.replace(/\s+/g, '');
};

const normalizeEmail = (email?: string | null): string => {
  if (!email) return '';
  return email.trim().toLowerCase();
};

const DEFAULT_PET_VALUE = 19;

const formatPetValueForInput = (value: number): string => {
  const formatted = value.toFixed(2);
  return formatted.replace('.', ',');
};

const parsePetValueInput = (value: string): number | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.includes(',')) {
    const normalized = trimmed.replace(/\./g, '').replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const applyPetValueToRecord = (record: PetPlanRecord, unitValue: number): PetPlanRecord => {
  const safeValue = Number.isFinite(unitValue) ? unitValue : 0;
  const premium = Math.max(record.dependentsCount, 0) * safeValue;
  return {
    ...record,
    premium,
    amount: record.copay + premium,
  };
};

const calculateTotalsFromRecords = (records: PetPlanRecord[]) => {
  return records.reduce(
    (acc, record) => ({
      dependents: acc.dependents + record.dependentsCount,
      feeCount: acc.feeCount + record.feeCount,
      amount: acc.amount + record.amount,
    }),
    { dependents: 0, feeCount: 0, amount: 0 },
  );
};

const formatCpf = (cpf?: string | null): string => {
  if (!cpf) return '';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

function MappingCombobox({
  value,
  options,
  disabled,
  onChange,
}: {
  value?: string | null;
  options: CollaboratorReference[];
  disabled?: boolean;
  onChange: (id: string | null) => void;
}) {
  const [query, setQuery] = useState('');
  const selected = options.find((option) => option.id === value) ?? null;
  const deferredQuery = useDeferredValue(query);
  const filtered = useMemo(() => {
    const normalizedQuery = normalizeString(deferredQuery);
    if (!normalizedQuery) return options;
    return options.filter((option) => option.normalizedName.includes(normalizedQuery));
  }, [options, deferredQuery]);

  return (
    <Combobox value={selected} onChange={(option) => onChange(option?.id ?? null)} disabled={disabled}>
      {({ open }) => (
        <div className="relative w-full min-w-[16rem]">
          <Combobox.Input
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            displayValue={(option: CollaboratorReference | null) => option?.name ?? ''}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
            placeholder="Selecionar colaborador..."
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>
          <Transition
            as={Fragment}
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filtered.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-500">Nenhum resultado</div>
              ) : (
                filtered.map((person) => (
                  <Combobox.Option
                    key={person.id}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary-600 text-white' : 'text-gray-900'
                      }`
                    }
                    value={person}
                  >
                    {({ selected: isSelected, active }) => (
                      <>
                        <span className="block">
                          <span className={`block ${isSelected ? 'font-semibold text-gray-900' : 'font-normal text-gray-900'}`}>
                            {person.name}
                          </span>
                          {person.emails.length ? (
                            <span className="block text-xs text-gray-500">{person.emails[0]}</span>
                          ) : null}
                          {person.cpf ? (
                            <span className="block text-xs text-gray-500">{formatCpf(person.cpf)}</span>
                          ) : null}
                        </span>
                        {isSelected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-primary-600'
                              }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}

function StatusBadge({ status }: { status: PetPlanMatchStatus }) {
  const statusMap: Record<PetPlanMatchStatus, { text: string; className: string; Icon: typeof CheckCircleIcon }> = {
    auto_code: { text: 'Auto (inscrição)', className: 'bg-green-100 text-green-700', Icon: CheckCircleIcon },
    auto_name: { text: 'Auto (nome)', className: 'bg-green-100 text-green-700', Icon: CheckCircleIcon },
    manual: { text: 'Manual', className: 'bg-blue-100 text-blue-700', Icon: CheckCircleIcon },
    not_found: { text: 'Sem match', className: 'bg-amber-100 text-amber-700', Icon: ExclamationTriangleIcon },
  };
  const data = statusMap[status];
  const Icon = data.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${data.className}`}>
      <Icon className="h-4 w-4" aria-hidden />
      {data.text}
    </span>
  );
}

const PET_PLAN_PROVIDER = {
  name: 'Galpeco',
  slug: 'galpeco',
};

export function PetPlanImportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success: showSuccess, error: showError } = useToast();
  const [billingPeriod, setBillingPeriod] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [petValueInput, setPetValueInput] = useState(() => formatPetValueForInput(DEFAULT_PET_VALUE));
  const [petValue, setPetValue] = useState<number>(DEFAULT_PET_VALUE);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [matches, setMatches] = useState<PetPlanMatch[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [totals, setTotals] = useState({ dependents: 0, feeCount: 0, amount: 0 });
  const [codeAssignments, setCodeAssignments] = useState<Record<string, string>>({});
  const { data: collaborators, isLoading: loadingCollaborators } = useCollaboratorsRealtimeQuery();

  const collaboratorRefs = useMemo<CollaboratorReference[]>(() => {
    return (collaborators || []).map((collaborator) => {
      const name = collaborator.name ?? 'Sem nome';
      const rawEmails = [
        collaborator.email,
        (collaborator as any).personalEmail,
        (collaborator as any).corporateEmail,
        (collaborator as any).companyEmail,
        (collaborator as any).workEmail,
        (collaborator as any).professionalEmail,
      ];
      const emails = rawEmails
        .map((emailValue) => (typeof emailValue === 'string' ? emailValue.trim() : ''))
        .filter((emailValue, index, array) => emailValue && array.indexOf(emailValue) === index);
      return {
        id: collaborator.id ?? '',
        name,
        cpf: (collaborator as any).cpf ?? null,
        normalizedName: normalizeString(name),
        emails,
        normalizedEmails: emails.map((emailValue) => emailValue.toLowerCase()),
        isActive: (collaborator as any).status !== 'desligado' && (collaborator as any).status !== 'inactive',
      };
    });
  }, [collaborators]);

  const collaboratorByName = useMemo(() => {
    const map = new Map<string, CollaboratorReference>();
    collaboratorRefs.forEach((collab) => {
      if (!collab.normalizedName || map.has(collab.normalizedName)) return;
      map.set(collab.normalizedName, collab);
    });
    return map;
  }, [collaboratorRefs]);

  const collaboratorByEmail = useMemo(() => {
    const map = new Map<string, CollaboratorReference>();
    collaboratorRefs.forEach((collab) => {
      collab.normalizedEmails.forEach((email) => {
        if (!email || map.has(email)) return;
        map.set(email, collab);
      });
    });
    return map;
  }, [collaboratorRefs]);

  const sourceFiles = useMemo(
    () =>
      files.map((file) => ({
        name: file.name,
        size: file.size,
        contentType: file.type || null,
      })),
    [files],
  );

  useEffect(() => {
    let active = true;
    const fetchAssignments = async () => {
      try {
        const data = await listPetPlanCodeAssignments();
        if (!active) return;
        setCodeAssignments(data);
      } catch (err) {
        console.error('[PetPlanImportPage] failed to load code assignments', err);
      }
    };
    void fetchAssignments();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!matches.length) return;
    setMatches((prev) => {
      let changed = false;
      const updated = prev.map((match) => {
        if (match.status !== 'not_found') return match;
        const normalizedCode = normalizeCodeKey(match.record.code);
        if (!normalizedCode) return match;
        const assignedId = codeAssignments[normalizedCode];
        if (assignedId) {
          const collaborator = collaboratorRefs.find((ref) => ref.id === assignedId);
          if (collaborator) {
            changed = true;
            return { ...match, collaboratorId: collaborator.id, status: 'auto_code' as PetPlanMatchStatus };
          }
        }
        const collaboratorFromEmail = collaboratorByEmail.get(normalizedCode);
        if (collaboratorFromEmail) {
          changed = true;
          return { ...match, collaboratorId: collaboratorFromEmail.id, status: 'auto_code' as PetPlanMatchStatus };
        }
        return match;
      });
      return changed ? updated : prev;
    });
  }, [codeAssignments, collaboratorByEmail, collaboratorRefs, matches]);

  useEffect(() => {
    if (!matches.length) return;
    setMatches((prev) => {
      const updated = prev.map((match) => ({
        ...match,
        record: applyPetValueToRecord(match.record, petValue),
      }));
      setTotals(calculateTotalsFromRecords(updated.map((item) => item.record)));
      return updated;
    });
  }, [petValue]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const handlePetValueInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setPetValueInput(value);
    const parsed = parsePetValueInput(value);
    setPetValue(parsed ?? 0);
  };

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files ? Array.from(event.target.files) : [];
    setFiles(fileList);
    setMatches([]);
    setTotals({ dependents: 0, feeCount: 0, amount: 0 });
    setParseError(null);
  };

  const handleParse = async () => {
    if (!files.length) {
      setParseError('Selecione o arquivo exportado pela Galpeco para continuar.');
      return;
    }
    setParsing(true);
    setParseError(null);
    try {
      const result = await parsePetPlanFile(files[0]!);
      const recordsWithPetValue = result.records.map((record) => applyPetValueToRecord(record, petValue));
      const normalizedMatches: PetPlanMatch[] = recordsWithPetValue.map((record) => {
        const normalizedCode = normalizeCodeKey(record.code);
        const assignedCollaboratorId = normalizedCode ? codeAssignments[normalizedCode] : null;
        let collaborator: CollaboratorReference | undefined;
        let status: PetPlanMatchStatus = 'not_found';
        if (assignedCollaboratorId) {
          collaborator = collaboratorRefs.find((ref) => ref.id === assignedCollaboratorId);
          if (collaborator) {
            status = 'auto_code';
          }
        }
        if (!collaborator && normalizedCode) {
          collaborator = collaboratorByEmail.get(normalizedCode);
          if (collaborator) {
            status = 'auto_code';
          }
        }
        if (!collaborator) {
          const normalizedName = normalizeString(record.name);
          collaborator = normalizedName ? collaboratorByName.get(normalizedName) : undefined;
          if (collaborator) {
            status = 'auto_name';
          }
        }
        return {
          record,
          collaboratorId: collaborator?.id ?? null,
          status,
        };
      });
      setMatches(normalizedMatches);
      setTotals(calculateTotalsFromRecords(recordsWithPetValue));
    } catch (error) {
      console.error('[PetPlanImportPage] parse error', error);
      setMatches([]);
      setTotals({ dependents: 0, feeCount: 0, amount: 0 });
      setParseError('Não foi possível ler o arquivo. Confirme se é o relatório oficial da Galpeco.');
    } finally {
      setParsing(false);
    }
  };

  const handleMatchChange = (index: number, collaboratorId: string | null) => {
    setMatches((prev) =>
      prev.map((match, matchIndex) => {
        if (matchIndex !== index) return match;
        return {
          ...match,
          collaboratorId,
          status: collaboratorId ? 'manual' : 'not_found',
        };
      }),
    );
  };

  const handleImport = async () => {
    const competence = billingPeriod.trim();
    if (!competence) {
      showError('Informe a competência (AAAA-MM).');
      return;
    }
    const consolidated = await isCompetenceConsolidated(competence);
    if (consolidated) {
      showError('Não é possível importar: competência já consolidada.');
      return;
    }
    if (!matches.length) {
      showError('Realize o match dos colaboradores antes de salvar a importação.');
      return;
    }
    if (!user) {
      showError('É necessário estar autenticado para importar.');
      return;
    }
    setSubmitting(true);
    try {
      const audit: PetPlanAuditItem[] = matches
        .map((match): PetPlanAuditItem | null => {
          if (match.status === 'not_found') {
            return {
              beneficiaryName: match.record.name,
              beneficiaryCpf: match.record.code,
              premium: match.record.amount,
              reason: 'Não vinculado a colaborador',
              type: 'not_found',
            };
          }
          const collaborator = collaboratorRefs.find((c) => c.id === match.collaboratorId);
          if (collaborator && !collaborator.isActive) {
            return {
              beneficiaryName: match.record.name,
              beneficiaryCpf: match.record.code,
              premium: match.record.amount,
              reason: 'Colaborador inativo',
              type: 'inactive',
            };
          }
          return null;
        })
        .filter((item): item is PetPlanAuditItem => item !== null);

      await createPetPlanImport(
        PET_PLAN_PROVIDER.slug,
        {
          billingPeriod: competence,
          totals,
          sourceFiles,
          recordsPreview: matches.map((match) => ({
            ...match.record,
            collaboratorId: match.collaboratorId,
          })),
          audit,
        },
        user,
      );

      const assignmentsToSave = matches
        .filter((match) => match.collaboratorId)
        .map((match) => ({
          code: normalizeCodeKey(match.record.code),
          collaboratorId: match.collaboratorId!,
        }))
        .filter(
          (assignment) => assignment.code && codeAssignments[assignment.code] !== assignment.collaboratorId,
        );

      if (assignmentsToSave.length) {
        await savePetPlanCodeAssignments(assignmentsToSave);
        setCodeAssignments((prev) => {
          const next = { ...prev };
          assignmentsToSave.forEach(({ code, collaboratorId }) => {
            next[code] = collaboratorId;
          });
          return next;
        });
      }
      showSuccess('Importação salva com sucesso.');
      navigate('/expenses/benefits/pet-plan');
    } catch (error) {
      console.error('[PetPlanImportPage] failed to save import', error);
      showError('Não foi possível salvar a importação.');
    } finally {
      setSubmitting(false);
    }
  };

  const sortedMatches = useMemo(() => {
    const priority: Record<PetPlanMatchStatus, number> = {
      not_found: 0,
      manual: 1,
      auto_code: 2,
      auto_name: 3,
    };
    return matches
      .map((match, originalIndex) => ({ match, originalIndex }))
      .sort((a, b) => {
        const diff = priority[a.match.status] - priority[b.match.status];
        if (diff !== 0) return diff;
        const nameA = a.match.record.name.toLowerCase();
        const nameB = b.match.record.name.toLowerCase();
        const nameDiff = nameA.localeCompare(nameB, 'pt-BR');
        if (nameDiff !== 0) return nameDiff;
        return a.originalIndex - b.originalIndex;
      });
  }, [matches]);

  const formatCurrency = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="space-y-6">
      <SubPageHeader
        backTo="/expenses/benefits/pet-plan"
        icon={<PetPawIcon className="h-5 w-5" aria-hidden />}
        badge="Benefícios"
        title="Importar plano pet"
        description="Selecione a operadora e envie o arquivo do período desejado."
      />

      <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Operadora</label>
            <select
              value={PET_PLAN_PROVIDER.slug}
              disabled
              className="mt-1 w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed"
            >
              <option value={PET_PLAN_PROVIDER.slug}>{PET_PLAN_PROVIDER.name}</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">Galpeco é a operadora inicial deste fluxo do plano pet.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Competência (AAAA-MM)</label>
            <input
              type="month"
              value={billingPeriod}
              onChange={(event) => setBillingPeriod(event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">Use o mês de referência presente no arquivo da Galpeco.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Valor por pet</label>
            <input
              type="text"
              value={petValueInput}
              onChange={handlePetValueInputChange}
              placeholder="19,00"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">É o valor que a Raro subsidia por cada pet.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Arquivo</label>
            <label className="mt-1 flex cursor-pointer items-center justify-between rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm transition hover:border-primary-400">
              <span className="inline-flex items-center gap-2">
                <CloudArrowUpIcon className="h-5 w-5 text-primary-600" aria-hidden />
                {files.length ? `${files.length} arquivo(s) selecionado(s)` : 'Selecionar arquivos (.xlsx, .csv)'}
              </span>
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" multiple onChange={handleFilesChange} />
            </label>
            <p className="mt-1 text-xs text-gray-500">Envie o arquivo consolidado disponibilizado pela operadora.</p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleParse}
            disabled={!files.length || parsing}
            className="inline-flex items-center gap-2 rounded-md border border-primary-200 bg-white px-3 py-2 text-sm font-semibold text-primary-700 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {parsing ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                Lendo arquivo...
              </>
            ) : (
              'Conferir'
            )}
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={submitting || parsing || matches.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                Salvando
              </>
            ) : (
              <>
                Importar
                <ArrowRightIcon className="h-4 w-4" aria-hidden />
              </>
            )}
          </button>
        </div>
      </form>

      {parseError ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{parseError}</div>
      ) : null}

      {sortedMatches.length > 0 ? (
        <div className="space-y-3 rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Titulares (prévia)</h2>
              <p className="text-sm text-gray-600">
                Revise as inscrições importadas, confira totais e associe cada registro a um colaborador.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span>Encontrados: {sortedMatches.length}</span>
              <span>Dependentes totais: {totals.dependents}</span>
              <span>Taxas totais: {totals.feeCount}</span>
              <span>Valor total: {formatCurrency(totals.amount)}</span>
            </div>
          </div>

          <div className="overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Titular</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Inscrição</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Qtd. dependentes</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Qtd. taxas</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Copay</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Prêmio</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Valor total</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Match</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Colaborador</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {sortedMatches.map(({ match, originalIndex }) => (
                  <tr key={`${match.record.code}-${originalIndex}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-900">{match.record.name}</div>
                    </td>
                    <td className="px-3 py-2 font-semibold text-gray-900">{match.record.code}</td>
                    <td className="px-3 py-2 text-gray-800">{match.record.dependentsCount}</td>
                    <td className="px-3 py-2 text-gray-800">{match.record.feeCount}</td>
                    <td className="px-3 py-2 text-gray-800">{formatCurrency(match.record.copay)}</td>
                    <td className="px-3 py-2 text-gray-800">{formatCurrency(match.record.premium)}</td>
                    <td className="px-3 py-2 text-gray-900">{formatCurrency(match.record.amount)}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={match.status} />
                    </td>
                    <td className="px-3 py-2">
                      <MappingCombobox
                        value={match.collaboratorId}
                        options={collaboratorRefs}
                        disabled={loadingCollaborators}
                        onChange={(id) => handleMatchChange(originalIndex, id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
